import { useEffect, useRef, useState } from "react";

/**
 * CursorGlow — a soft, lag-following golden aurora that tracks the mouse.
 * Uses raf-driven lerp for buttery smoothness. Auto-disables on touch devices
 * and when the user prefers reduced motion.
 */
export default function CursorGlow() {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const isCoarse = window.matchMedia("(pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isCoarse || reduced) return;
    setEnabled(true);

    // Start slightly off-screen
    let tx = -600,
      ty = -600,
      cx = tx,
      cy = ty;
    let raf;

    const onMove = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };

    const tick = () => {
      // Ease-out follow
      cx += (tx - cx) * 0.14;
      cy += (ty - cy) * 0.14;
      if (ref.current) {
        ref.current.style.transform = `translate3d(${cx - 320}px, ${cy - 320}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, []);

  if (!enabled) return null;

  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="cursor-glow"
      className="pointer-events-none fixed top-0 left-0 z-[45] w-[640px] h-[640px] rounded-full"
      style={{
        background:
          "radial-gradient(closest-side, rgba(212,175,55,0.14) 0%, rgba(212,175,55,0.06) 30%, rgba(212,175,55,0.02) 55%, transparent 75%)",
        mixBlendMode: "plus-lighter",
        willChange: "transform",
        filter: "blur(2px)",
      }}
    />
  );
}
